import { atom } from 'jotai'
import type { Atom } from 'jotai'
import { atomEffect } from 'jotai-effect'
import { atomWithQuery } from 'jotai-tanstack-query'
import type { TrustedGuestRequest, TrustedGuestResponse } from '~/lib/member/member'
import { MEMBER_STALE_TIME, callTrustedGuest } from '~/lib/member/member'
import { env$ } from '~/stores/env'
import { email$, reserverPhoneNumber$ } from './stateMachine/context'
import { isGuest$, isInitialDisplay$, transition$ } from './stateMachine/state'
import type { ReservationInputEvent } from './stateMachine/statemachine'
import {
  emailError$,
  reserverName$,
  reserverNameError$,
  reserverPhoneNumberError$,
} from './userInput'

type TrustedGuestQueryKey = readonly [string, string, TrustedGuestRequest]
const trustedGuestQueryKey$: Atom<TrustedGuestQueryKey> = atom((get) => {
  const env = get(env$)
  return ['trustedGuest', env.MEMBER_ORIGIN, get(trustedGuestQueryVariables$)]
})
trustedGuestQueryKey$.debugLabel = 'trustedGuestQueryKey$'

export const trustedGuestQueryFn$ = atom({
  fn: async ({ queryKey: [, origin, variables] }: { queryKey: TrustedGuestQueryKey }) =>
    callTrustedGuest(origin, variables),
})
trustedGuestQueryFn$.debugLabel = 'trustedGuestFn$'

const trustedGuestQueryVariables$: Atom<TrustedGuestRequest> = atom((get) => {
  const name = get(reserverName$) ?? ''
  const phoneNumber = get(reserverPhoneNumber$) ?? ''
  const mailAddress = get(email$) ?? ''
  return {
    name,
    phoneNumber,
    mailAddress,
  }
})
trustedGuestQueryVariables$.debugLabel = 'trustedGuestQueryVariables$'

const trustedGuestQueryEnabled$ = atom((get) => {
  const isGuest = get(isGuest$)
  const name = get(reserverName$)
  const nameError = get(reserverNameError$)
  const phoneNumber = get(reserverPhoneNumber$)
  const phoneNumberError = get(reserverPhoneNumberError$)
  const email = get(email$)
  const emailError = get(emailError$)
  return trustedGuestQueryEnabled(isGuest, {
    name,
    nameError,
    phoneNumber,
    phoneNumberError,
    email,
    emailError,
  })
})
trustedGuestQueryEnabled$.debugLabel = 'trustedGuestQueryEnabled$'
function trustedGuestQueryEnabled(
  isGuest: boolean,
  data: {
    name: string
    nameError: string
    phoneNumber: string
    phoneNumberError: string
    email: string
    emailError: string
  }
) {
  if (!isGuest) {
    return false
  }
  // member への無駄打ちを防ぐため、各入力項目が未入力（undefined）またはエラーメッセージが表示されている場合はリクエストしない
  return (
    data.name !== '' &&
    data.nameError === '' &&
    data.phoneNumber !== '' &&
    data.phoneNumberError === '' &&
    data.email !== '' &&
    data.emailError === ''
  )
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('trustedGuestQueryEnabled', () => {
    const name = 'いっきゅうたろう'
    const nameError = '氏名は必須項目です'
    const phoneNumber = '09012345678'
    const phoneNumberError = '電話番号が不正です'
    const email = 'ikyu@ikyu.com'
    const emailError = 'メールアドレスが不正です'
    const blank = {
      name: '',
      phoneNumber: '',
      email: '',
    }
    expect(
      trustedGuestQueryEnabled(false, {
        ...blank,
        nameError,
        emailError,
        phoneNumberError,
      })
    ).toBe(false)
    expect(
      trustedGuestQueryEnabled(true, {
        ...blank,
        nameError,
        emailError,
        phoneNumberError,
      })
    ).toBe(false)
    expect(
      trustedGuestQueryEnabled(true, { ...blank, nameError: '', emailError, phoneNumberError })
    ).toBe(false)
    expect(
      trustedGuestQueryEnabled(true, {
        ...blank,
        nameError,
        emailError,
        phoneNumberError: '',
      })
    ).toBe(false)
    expect(
      trustedGuestQueryEnabled(true, { ...blank, nameError, phoneNumberError, emailError: '' })
    ).toBe(false)
    expect(
      trustedGuestQueryEnabled(true, {
        name,
        nameError: '',
        phoneNumber,
        phoneNumberError: '',
        email,
        emailError: '',
      })
    ).toBe(true)
  })
}

const trustedGuestQuery$ = atomWithQuery((get) => ({
  queryKey: get(trustedGuestQueryKey$),
  queryFn: get(trustedGuestQueryFn$).fn,
  staleTime: MEMBER_STALE_TIME,
  enabled: get(trustedGuestQueryEnabled$),
}))
trustedGuestQuery$.debugLabel = 'trustedGuestQuery$'

// ../specification.md 「atomEffect について」参照
export const listenTrustedGuest$ = atomEffect((get, set) => {
  if (get(isInitialDisplay$)) {
    return
  }
  // クエリが無効化された場合は、untrusted イベントを発火する
  if (!get(trustedGuestQueryEnabled$)) {
    set(transition$, { type: 'untrusted' })
    return
  }

  const { status, data } = get(trustedGuestQuery$)
  if (status === 'pending') {
    return
  }
  // エラーの場合は、信頼済として扱う
  // @see: https://github.com/ikyucom/macaron-frontend/pull/2137#discussion_r1461002168
  if (status === 'error') {
    set(transition$, { type: 'trusted' })
    return
  }
  const event = nextEventOnLoad(data)
  set(transition$, event)
})
listenTrustedGuest$.debugLabel = 'listenTrustedGuest$'

function nextEventOnLoad(
  data: TrustedGuestResponse
): Extract<ReservationInputEvent, { type: 'trusted' } | { type: 'untrusted' }> {
  return data.trusted ? { type: 'trusted' } : { type: 'untrusted' }
}
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest
  it('nextEventOnLoad(TrustedGuest)', () => {
    expect(nextEventOnLoad({ trusted: true })).toEqual({ type: 'trusted' })
    expect(nextEventOnLoad({ trusted: false })).toEqual({ type: 'untrusted' })
  })
}
